import type {
  Analytics,
  AnalyticsRange,
  BatchDetail,
  BatchSummary,
  BlobSettings,
  BlobTestResult,
  CheckContactsResult,
  CosmosSettings,
  CosmosTestResult,
  CreateContactsResult,
  Destination,
  ImportFilesInput,
  Invite,
  LlmProvider,
  LlmSettings,
  LogEntry,
  Paged,
  Role,
  Stats,
  StatusBucket,
  User,
} from '@ia/shared';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

let onUnauthorized: (() => void) | null = null;
/** Registered by the auth provider; called on any 401 from a protected endpoint. */
export function setUnauthorizedHandler(fn: (() => void) | null) {
  onUnauthorized = fn;
}

/** Endpoints where a 401 is an expected answer and must not trigger a redirect. */
const NO_REDIRECT = ['/api/auth/me', '/api/auth/login', '/api/setup', '/api/auth/accept-invite', '/api/invites/lookup/'];

type Query = Record<string, string | number | boolean | null | undefined>;

function qs(q?: Query): string {
  if (!q) return '';
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === null || v === '') continue;
    p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : '';
}

async function request<T>(method: string, path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (method !== 'GET') headers['X-Requested-With'] = 'ia-admin';
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  let res: Response;
  try {
    res = await fetch(path, {
      method,
      headers,
      credentials: 'include',
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });
  } catch (e) {
    if ((e as Error).name === 'AbortError') throw e;
    throw new ApiError(0, 'network_error', 'Could not reach the server. Check that it is running.');
  }
  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = null;
    }
  }
  if (!res.ok) {
    const err = (data ?? {}) as { error?: string; message?: string };
    if (res.status === 401 && !NO_REDIRECT.some((p) => path.startsWith(p))) onUnauthorized?.();
    throw new ApiError(res.status, err.error ?? `http_${res.status}`, err.message ?? (res.statusText || `Request failed (${res.status})`));
  }
  return data as T;
}

export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error) return e.message;
  return String(e);
}

export type LlmTestResult = { ok: boolean; model: string; latencyMs: number; error?: string };

export const api = {
  setupStatus: () => request<{ needsSetup: boolean }>('GET', '/api/setup/status'),
  setup: (b: { name: string; email: string; password: string }) => request<{ user: User }>('POST', '/api/setup', b),
  login: (b: { email: string; password: string }) => request<{ user: User }>('POST', '/api/auth/login', b),
  logout: () => request<{ ok: true }>('POST', '/api/auth/logout'),
  me: () => request<{ user: User }>('GET', '/api/auth/me'),
  lookupInvite: (token: string) =>
    request<{ email: string; role: Role; expiresAt: string }>('GET', `/api/invites/lookup/${encodeURIComponent(token)}`),
  acceptInvite: (b: { token: string; name: string; password: string }) =>
    request<{ user: User }>('POST', '/api/auth/accept-invite', b),

  users: () => request<User[]>('GET', '/api/users'),
  updateUser: (id: string, b: { role?: Role; disabled?: boolean }) => request<User>('PATCH', `/api/users/${id}`, b),
  invites: () => request<Invite[]>('GET', '/api/invites'),
  createInvite: (b: { email: string; role: Role }) => request<Invite>('POST', '/api/invites', b),
  revokeInvite: (id: string) => request<{ ok: true }>('DELETE', `/api/invites/${id}`),

  stats: (signal?: AbortSignal) => request<Stats>('GET', '/api/stats', undefined, signal),
  analytics: (range: AnalyticsRange, signal?: AbortSignal) =>
    request<Analytics>('GET', `/api/analytics${qs({ range, tz: new Date().getTimezoneOffset() })}`, undefined, signal),
  batches: (
    q: { bucket?: StatusBucket; destinationId?: string; q?: string; page?: number; pageSize?: number },
    signal?: AbortSignal,
  ) => request<Paged<BatchSummary>>('GET', `/api/batches${qs(q)}`, undefined, signal),
  batch: (id: string, signal?: AbortSignal) => request<BatchDetail>('GET', `/api/batches/${id}`, undefined, signal),
  batchLogs: (id: string, q: { runId?: string; level?: string; after?: number }, signal?: AbortSignal) =>
    request<LogEntry[]>('GET', `/api/batches/${id}/logs${qs(q)}`, undefined, signal),
  replay: (id: string) => request<BatchDetail>('POST', `/api/batches/${id}/replay`),
  process: (id: string) => request<BatchDetail>('POST', `/api/batches/${id}/process`),
  importFiles: (b: ImportFilesInput) => request<BatchDetail>('POST', '/api/imports', b),
  workerResult: (id: string) => request<BatchDetail>('POST', `/api/batches/${id}/worker-result`),
  checkContacts: (id: string) => request<BatchDetail & { result: CheckContactsResult }>('POST', `/api/batches/${id}/contacts/check`),
  createContacts: (id: string) => request<BatchDetail & { result: CreateContactsResult }>('POST', `/api/batches/${id}/contacts`),

  destinations: () => request<Destination[]>('GET', '/api/destinations'),
  updateDestination: (id: string, b: { label?: string | null; tenantId?: string | null; pat?: string | null }) =>
    request<Destination>('PATCH', `/api/destinations/${id}`, b),

  blobSettings: () => request<BlobSettings>('GET', '/api/settings/blob'),
  updateBlobSettings: (b: { container: string; prefix: string; workerContainer: string; connectionString?: string | null }) =>
    request<BlobSettings>('PUT', '/api/settings/blob', b),
  testBlob: () => request<BlobTestResult>('POST', '/api/settings/blob/test'),

  cosmosSettings: () => request<CosmosSettings>('GET', '/api/settings/cosmos'),
  updateCosmosSettings: (b: { database: string; container: string; connectionString?: string | null }) =>
    request<CosmosSettings>('PUT', '/api/settings/cosmos', b),
  testCosmos: () => request<CosmosTestResult>('POST', '/api/settings/cosmos/test'),

  llmSettings: () => request<LlmSettings>('GET', '/api/settings/llm'),
  updateLlmSettings: (b: { enabled: boolean; provider: LlmProvider; model: string; apiKey?: string | null; baseUrl?: string | null }) =>
    request<LlmSettings>('PUT', '/api/settings/llm', b),
  testLlm: () => request<LlmTestResult>('POST', '/api/settings/llm/test'),
};
